import { Lock } from "lucide-react";
import { usePermissions } from "@/data/PermissionsContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  /** Permission key(s) to check — any single match grants access */
  permission: string | string[];
  children: React.ReactElement;
  message?: string;
}

/**
 * Wraps any button/action. If the user lacks the required permission,
 * the button is rendered as disabled with a tooltip explaining why.
 * If the user has permission, children render unchanged.
 */
export function PermissionGate({ permission, children, message = "You don't have permission for this action" }: Props) {
  const { hasPermission } = usePermissions();

  const allowed = Array.isArray(permission)
    ? permission.some((p) => hasPermission(p))
    : hasPermission(permission);

  if (allowed) return children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex cursor-not-allowed opacity-50"
          style={{ pointerEvents: "none" }}
          aria-disabled="true"
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="flex items-center gap-1.5 text-xs max-w-[220px]">
        <Lock className="h-3 w-3 shrink-0" />
        {message}
      </TooltipContent>
    </Tooltip>
  );
}
